import { applyCSSOverrides } from '@src/utility/styled';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
  DialogCard,
  DialogFooter,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '../common/dialogStyles';
import { CopyIcon } from '../common/icons';
import Textarea from '../common/Textarea';

type OverridesConfig = {
  hideInputLabel?: boolean;
  saveButtonContent?: React.ReactNode;
  wrapperStyles?: Record<string, string | number>;
};

type Props = {
  name: string;
  tag: string;
  type: 'save_as' | 'rename';
  onCancel: () => void;
  onSave: (name: string) => void;
  modalOverrides?: OverridesConfig;
};

const CopyContentModal: React.FC<Props> = (props: Props) => {
  const [nameValue, setNameValue] = useState(props.name);
  const { modalOverrides = {} } = props;
  const displayTag = props.tag === 'Blog' ? 'Article' : props.tag;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    // The field wraps over several lines but still holds a single-line name, so
    // Enter commits the dialog rather than inserting a break.
    if (e.key === 'Enter') {
      e.preventDefault();
      props.onSave(nameValue);
    }
  };

  return (
    <Card overrides={modalOverrides.wrapperStyles}>
      <DialogTitle>
        {props.type === 'save_as' ? 'Copy' : 'Rename'} {displayTag}
      </DialogTitle>
      <NameField
        autoGrow
        fullWidth
        label={modalOverrides.hideInputLabel ? undefined : 'Name'}
        value={nameValue}
        onKeyDown={handleKeyDown}
        // Pasted text can carry line breaks; a name never does.
        onChange={e => setNameValue(e.target.value.replace(/[\r\n]+/g, ' '))}
      />
      <DialogFooter>
        <SecondaryButton type="button" onClick={props.onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="button" onClick={() => props.onSave(nameValue)}>
          {modalOverrides.saveButtonContent || (
            <>
              <CopyIcon /> Save
            </>
          )}
        </PrimaryButton>
      </DialogFooter>
    </Card>
  );
};

const Card = styled(DialogCard)<{
  overrides?: Record<string, string | number>;
}>`
  ${props => applyCSSOverrides(props.overrides)}
`;

// Keeps the label/field gap the dialog had before, one step tighter than the
// card's inter-section gap. `&&` so it beats the primitive's own wrapper rule
// regardless of stylesheet order.
const NameField = styled(Textarea)`
  && {
    gap: var(--spacing-sm);
  }
`;

export default CopyContentModal;
